import React, { useEffect, useState } from "react";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  Plus,
  Eraser,
  Building2,
} from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";
import { hasPermission } from "../../../utils/permissionUtils";
import { modalSelectPortalProps } from "../../../components/ui/customerSelectInModal";

const TEXT = {
  searchPlaceholder: "Tìm theo tên, địa chỉ, email, điện thoại, người đại diện...",
  refresh: "Làm mới",
  advanced: "Lọc nâng cao",
  advancedTitle: "Bộ lọc nâng cao",
  affiliation: "Loại hình",
  allAffiliation: "Tất cả loại hình",
  status: "Trạng thái",
  allStatus: "Tất cả trạng thái",
  clear: "Xóa lọc",
  apply: "Áp dụng",
  addBranch: "Thêm chi nhánh",
};

const AFFILIATION_OPTIONS = [
  { value: "", label: "Tất cả loại hình" },
  { value: "hq", label: "Trụ sở / HQ" },
  { value: "affiliated", label: "Trực thuộc" },
  { value: "franchise", label: "Nhượng quyền" },
];

const STATUS_OPTIONS = [
  { value: "", label: "Tất cả trạng thái" },
  { value: "true", label: "Đang hoạt động" },
  { value: "false", label: "Ngưng nhận" },
];

const BranchFilter = ({ filters, onChange, onNewClick, onRefresh }) => {
  const [advanced, setAdvanced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const update = (name, value) => {
    if (name === "affiliation_type") {
      onChange({ ...filters, affiliation_type: value, dashboard: "" });
      return;
    }
    onChange({ ...filters, [name]: value });
  };

  const clearFilters = () => {
    onChange({
      keyword: "",
      affiliation_type: "",
      is_active: "",
      dashboard: "",
    });
  };

  const isFiltering = Boolean(
    filters.keyword?.trim() ||
      filters.affiliation_type ||
      filters.is_active ||
      filters.dashboard
  );

  const handleRefresh = () => {
    onRefresh?.();
  };

  const selectStyles = {
    ...customSelectStyles,
    control: (provided, state) => ({
      ...(typeof customSelectStyles.control === "function"
        ? customSelectStyles.control(provided, state)
        : provided),
      minHeight: 48,
      borderRadius: 16,
      borderColor: state.isFocused ? "#ef4444" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 4px rgba(239, 68, 68, 0.05)" : "none",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  };

  const ActionButton = ({ icon: Icon, onClick, title, active = false }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border transition-all duration-200 ${
        active
          ? "border-red-200 bg-red-50 text-red-600 shadow-sm"
          : "border-gray-200 bg-white text-gray-500 hover:border-red-400 hover:text-red-500"
      }`}
    >
      <Icon size={18} />
    </button>
  );

  const searchRow = (
    <div className="flex w-full min-w-0 items-stretch gap-2">
      <div className="relative min-w-0 flex-1">
        <Search
          className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-400"
          strokeWidth={2.25}
        />
        <input
          type="search"
          placeholder={TEXT.searchPlaceholder}
          value={filters.keyword}
          onChange={(e) => update("keyword", e.target.value)}
          className="w-full rounded-2xl border border-gray-200 bg-white py-3 pl-11 pr-3 text-sm font-semibold text-gray-800 shadow-inner outline-none transition placeholder:text-gray-400 focus:border-red-300 focus:ring-4 focus:ring-red-50"
        />
      </div>
      <ActionButton icon={RotateCw} onClick={handleRefresh} title={TEXT.refresh} />
      <ActionButton
        icon={SlidersHorizontal}
        onClick={() => setAdvanced(!advanced)}
        title={TEXT.advanced}
        active={advanced}
      />
    </div>
  );

  const advancedFields = (
    <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-end">
      <div className="w-full min-w-[200px] flex-1 md:max-w-xs">
        <label className="mb-1.5 ml-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
          {TEXT.affiliation}
        </label>
        <Select
          options={AFFILIATION_OPTIONS}
          placeholder={TEXT.allAffiliation}
          styles={selectStyles}
          value={
            AFFILIATION_OPTIONS.find(
              (x) => x.value === (filters.affiliation_type || "")
            ) || null
          }
          onChange={(opt) => update("affiliation_type", opt?.value ?? "")}
          {...modalSelectPortalProps}
        />
      </div>
      <div className="w-full min-w-[200px] flex-1 md:max-w-xs">
        <label className="mb-1.5 ml-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
          {TEXT.status}
        </label>
        <Select
          options={STATUS_OPTIONS}
          placeholder={TEXT.allStatus}
          styles={selectStyles}
          value={
            STATUS_OPTIONS.find((x) => x.value === (filters.is_active || "")) || null
          }
          onChange={(opt) => update("is_active", opt?.value ?? "")}
          {...modalSelectPortalProps}
        />
      </div>
      {isFiltering ? (
        <button
          type="button"
          onClick={clearFilters}
          title={TEXT.clear}
          className="flex h-12 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-xs font-bold uppercase tracking-wide text-gray-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700"
        >
          <Eraser size={16} />
          {TEXT.clear}
        </button>
      ) : null}
    </div>
  );

  if (isMobile) {
    return (
      <div className="relative mb-4 space-y-3">
        <div className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm">
          <div className="mb-3 flex items-center gap-2 border-b border-gray-50 pb-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50 text-red-600">
              <Building2 size={18} strokeWidth={2.25} />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                Tìm kiếm
              </p>
              <p className="text-xs font-bold text-gray-600">Chi nhánh</p>
            </div>
          </div>
          {searchRow}
        </div>

        {hasPermission("hrm.branches", "C") && (
          <button
            type="button"
            onClick={onNewClick}
            title={TEXT.addBranch}
            aria-label={TEXT.addBranch}
            className="fixed bottom-20 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-red-600 to-red-700 text-xl text-white shadow-lg transition-all hover:scale-105 active:scale-95"
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        )}

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title={TEXT.advancedTitle}
          footer={
            <button
              type="button"
              onClick={() => setAdvanced(false)}
              className="w-full rounded-xl bg-red-600 py-3 text-sm font-black uppercase tracking-widest text-white shadow-md"
            >
              {TEXT.apply}
            </button>
          }
        >
          {advancedFields}
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0 flex-1 space-y-3">
          {searchRow}
          <AnimatePresence initial={false}>
            {advanced && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                {advancedFields}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {hasPermission("hrm.branches", "C") && (
          <button
            type="button"
            onClick={onNewClick}
            className="inline-flex h-12 shrink-0 items-center justify-center gap-2 self-start rounded-xl bg-gradient-to-r from-red-700 to-red-800 px-5 text-sm font-bold uppercase tracking-wide text-white shadow-md transition hover:shadow-lg lg:self-end"
          >
            <Plus size={18} strokeWidth={2.5} />
            {TEXT.addBranch}
          </button>
        )}
      </div>
    </div>
  );
};

export default BranchFilter;
